import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages } from '@/lib/supabasePages';
import type { Json } from '@/integrations/supabase/types';
import { toast } from 'sonner';
// NOTE: the organize layout (arrangeInPlace) is dynamically imported inside autoOrganizeItems()
// to break the circular dependency: canvasStore -> clusterLayout -> canvasStore (type)
import type { BoardProfile } from '@/lib/boardProfileService';
import {
  clusterIdFor,
  groupIntoManualCluster,
  keepManualClusters,
  moveBetweenClusters,
  removeFromClusters,
  toSavedClusters,
  type AICluster,
  type SpatialCluster,
} from '@/lib/spatialClustering';
import { useHistoryStore, type HistoryGroup } from '@/stores/historyStore';
import { CANVAS_MIN_BOUNDS, MIN_ITEM_Y } from '@/lib/canvasConstants';
import { extractItemText } from '@/lib/itemTextExtractor';
import { onSubjectsTagged, queueSubjectTagging } from '@/lib/library/subjectTagging';
import { isAwaitingPreview } from '../../supabase/functions/_shared/subjectInput';

/**
 * Save a board's named clusters to board_profiles.clusters. The profile row
 * may not exist yet (clusters are usually saved by the AI run that creates
 * it), so a missed update becomes an insert.
 */
async function saveBoardClusters(boardId: string, clusters: AICluster[]): Promise<boolean> {
  const { data, error } = await supabase
    .from('board_profiles')
    .update({ clusters: clusters as unknown as Json })
    .eq('board_id', boardId)
    .select('id');
  if (error) {
    console.warn('[Clustering] Failed to save clusters:', error);
    return false;
  }
  if (data && data.length > 0) return true;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { error: insertError } = await supabase
    .from('board_profiles')
    .insert({ board_id: boardId, user_id: user.id, clusters: clusters as unknown as Json });
  if (insertError) console.warn('[Clustering] Failed to create board profile for clusters:', insertError);
  return !insertError;
}

/** A board's saved clusters (all of them, including AI groups a hand-made-only view doesn't draw). */
async function loadBoardClusters(boardId: string): Promise<AICluster[]> {
  const { data: profile } = await supabase
    .from('board_profiles')
    .select('clusters')
    .eq('board_id', boardId)
    .maybeSingle();
  return Array.isArray(profile?.clusters) ? (profile.clusters as unknown as AICluster[]) : [];
}

/** Compute a deterministic hash for a set of item IDs (for cluster caching) */
function computeClusterContentHash(itemIds: string[]): string {
  const sorted = [...itemIds].sort();
  // Simple hash: use sorted IDs joined + length suffix for collision avoidance
  const raw = sorted.join(',');
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return `${Math.abs(hash).toString(36)}_${sorted.length}`;
}

export type ItemType = 'sticky' | 'text_card' | 'document' | 'image' | 'file' | 'board_link' | 'link' | 'video' | 'synced_item' | 'scraped_creative';
export type Tool = 'select' | 'pan' | 'board' | 'sticky' | 'text_card' | 'document' | 'image' | 'file' | 'link' | 'arrow';
export type ViewMode = 'discover' | 'focus';

export interface Board {
  id: string;
  parent_id: string | null;
  name: string;
  icon_color: string;
  canvas_position_x: number;
  canvas_position_y: number;
  canvas_zoom: number;
  created_at: string;
  updated_at: string;
  user_id?: string;
}

export interface BoardShare {
  id: string;
  board_id: string;
  shared_by: string;
  shared_with: string | null;
  share_token: string | null;
  permission: string | null;
  is_active: boolean | null;
  created_at: string | null;
  expires_at: string | null;
}

export interface Item {
  id: string;
  board_id: string;
  item_type: ItemType;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  z_index: number;
  content: Json;
  created_at: string;
  updated_at: string;
  user_id?: string;
  is_pinned_wall?: boolean | null;
  pinned_wall_at?: string | null;
  /** AI subject labels for the mobile Library's "By subject" spaces (items.subjects). */
  subjects?: string[] | null;
  // Ghost/streaming state (Phase 4)
  isGhost?: boolean;
  streamProgress?: number;
  ghostColor?: string;
}

export interface ItemInput {
  board_id?: string | null; // null for Wall items without a board
  item_type?: ItemType;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  z_index?: number;
  content?: Record<string, unknown>;
}

export interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

export interface CanvasState {
  // Navigation
  currentBoardId: string | null;
  boardPath: Board[];
  
  // View Mode
  viewMode: ViewMode;
  
  // Canvas state
  panX: number;
  panY: number;
  zoom: number;
  viewportWidth: number;
  viewportHeight: number;
  shouldFitToView: boolean; // Flag to trigger fit-to-view after board navigation
  pendingFocusItemId: string | null; // Item to center on instead of fitting all, after navigation
  isAutoOrganizing: boolean; // True during organize animation for CSS transitions
  isBoardTransitioning: boolean; // True during board navigation for fade transition
  lastBoardNavAt: number; // Timestamp of the last navigateToBoard call
  
  // Tools
  activeTool: Tool;
  hoverPreviewEnabled: boolean;
  
  // Selection
  selectedItemIds: Set<string>;
  newlyCreatedItemId: string | null;
  
  // Drag state for toolbar drag-to-canvas
  isDraggingFromToolbar: boolean;
  draggedToolType: Tool | null;
  
  // Drag-to-board state
  dropTargetBoardId: string | null;
  
  // Data
  boards: Board[];
  items: Item[];
  allItems: Item[]; // All items across all boards for Wall mode
  boardShares: BoardShare[]; // Shares for boards (to show shared indicator)
  
  // UI State
  isSidebarOpen: boolean;
  isDarkMode: boolean;
  isGridVisible: boolean;

  // Clipboard for copying items
  copiedItems: Item[];

  // Per-item layout constraints (client-only)
  itemMinHeights: Record<string, number>;
  
  // Active editor for formatting toolbar
  activeEditor: import('@tiptap/react').Editor | null;
  /** The item whose editor that is, for toolbar actions on the card itself (its background) */
  activeEditorItemId: string | null;
  isLoading: boolean;
  /** True once fetchAllItems has finished at least once (the phone Library's loading state). */
  allItemsLoaded: boolean;
  isSaving: boolean;
  isEditingItem: boolean; // True when editing text/documents to prevent drag
  wallScrollOffset: number; // Scroll offset for Wall/Discover view
  
  // Board semantic profile
  boardProfile: BoardProfile | null;
  isProfilingBoard: boolean;
  
  // Spatial clustering
  spatialClusters: SpatialCluster[];
  isClusteringEnabled: boolean;
  /**
   * 'board': the cluster toggle is on and every card belongs to a group.
   * 'manual': only groups the user made by hand are shown; the rest of the
   * board is left alone (no AI run, no "Unclustered", no re-layout).
   */
  clusterScope: 'board' | 'manual';
  isDetectingClusters: boolean;
  highlightedClusterId: string | null; // Cluster highlighted on item hover
  clusterToRename: string | null; // A cluster just grouped by hand: its label opens for typing
  lastClusteredItemIds: string[]; // Track item IDs from last clustering run
  
  // Computed helpers
  getBoundingBox: () => BoundingBox | null;
  getVisibleBoards: () => Board[];

  // Actions
  setCurrentBoard: (boardId: string | null) => void;
  navigateToBoard: (board: Board) => void;
  openItemOnCanvas: (item: Pick<Item, 'id' | 'board_id'>) => Promise<boolean>;
  navigateBack: (toBoardId: string | null) => void;
  goToFocusLanding: () => Promise<void>;
  setViewMode: (mode: ViewMode) => void;
  setPan: (x: number, y: number) => void;
  setZoom: (zoom: number) => void;
  setViewportSize: (width: number, height: number) => void;
  setActiveTool: (tool: Tool) => void;
  toggleHoverPreview: () => void;
  selectItem: (itemId: string, additive?: boolean) => void;
  clearSelection: () => void;
  toggleSidebar: () => void;
  toggleDarkMode: () => void;
  toggleGrid: () => void;
  setIsEditingItem: (isEditing: boolean) => void;
  setActiveEditor: (editor: import('@tiptap/react').Editor | null, itemId?: string | null) => void;
  setWallScrollOffset: (offset: number) => void;
  setDraggingFromToolbar: (isDragging: boolean, toolType?: Tool | null) => void;
  setDropTargetBoardId: (boardId: string | null) => void;
  moveItemsToBoard: (itemIds: string[], targetBoardId: string) => Promise<void>;
  /**
   * Moves an item from another board (e.g. one peeking out of a folder) onto
   * the current board at a canvas position. Returns the moved item, or null.
   */
  pullItemToCurrentBoard: (itemId: string, position: { x: number; y: number }) => Promise<Item | null>;
  setSelectedItems: (itemIds: Set<string>) => void;
  clearShouldFitToView: () => void;
  clearPendingFocusItem: () => void;

  // Layout constraint actions
  setItemMinHeight: (itemId: string, minHeight: number) => void;
  
  // Data actions
  fetchBoards: () => Promise<void>;
  repairMissingBoardLinks: () => Promise<void>;
  fetchItems: (boardId: string) => Promise<void>;
  fetchAllItems: () => Promise<void>;
  fetchBoardShares: () => Promise<void>;
  createBoard: (
    name: string,
    parentId: string | null,
    position?: { x: number; y: number },
    // id: pre-minted by Partnr so later steps can reference the folder
    options?: { id?: string; iconColor?: string },
  ) => Promise<Board | null>;
  updateBoard: (id: string, updates: Partial<Board>) => Promise<void>;
  deleteBoard: (id: string) => Promise<void>;
  createItem: (item: ItemInput) => Promise<Item | null>;
  /**
   * Create many items in one insert (Partnr's canvas actions). Returns the
   * created rows in input order and records one undo entry for all of them
   * (inside `history` when it names a group, none when it is false). Empty
   * array on failure.
   */
  createItems: (items: ItemInput[], options?: { history?: boolean | HistoryGroup }) => Promise<Item[]>;
  updateItem: (id: string, updates: Partial<Item>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  deleteItems: (ids: string[]) => Promise<void>;
  /** Put deleted items back exactly as they were (undo of a delete, redo of a create). */
  restoreItems: (items: Item[]) => Promise<void>;
  saveCanvasState: () => Promise<void>;
  clearNewlyCreatedItem: () => void;
  resetStore: () => void;

  // Ghost item management (Phase 4 — streaming placeholders)
  createGhost: (ghost: {
    id: string;
    itemType: ItemType;
    x: number;
    y: number;
    width: number;
    height: number;
    ghostColor?: string;
  }) => void;
  hydrateGhost: (ghostId: string, realData: {
    id: string;
    content: Record<string, unknown>;
  }) => void;
  updateGhostProgress: (ghostId: string, progress: number) => void;
  removeGhost: (ghostId: string) => void;
  
  // Copy/paste items
  copySelectedItems: () => void;
  pasteItems: (position: { x: number; y: number }) => Promise<Item[]>;
  hasCopiedItems: () => boolean;
  
  // Auto-organize
  autoOrganizeItems: (selectedItems: Item[]) => void;
  
  // Board profile
  refreshBoardProfile: () => Promise<void>;
  
  // Spatial clustering actions
  /** `layout: false` draws the groups without re-arranging the board. */
  detectSpatialClusters: (opts?: { layout?: boolean }) => Promise<void>;
  setClusteringEnabled: (enabled: boolean) => void;
  setSpatialClusters: (clusters: SpatialCluster[]) => void;
  getClusterForItem: (itemId: string) => SpatialCluster | null;
  getClusterMembers: (clusterId: string) => Item[];
  moveItemToCluster: (itemId: string, targetClusterId: string | null) => void;
  /** Group the given items into a new cluster of their own, saved with the board. */
  groupIntoCluster: (itemIds: string[]) => Promise<void>;
  /** Ungroup a cluster: its cards stay where they are, saved with the board. */
  ungroupCluster: (clusterId: string) => Promise<void>;
  /** Save the clusters on screen (after a rename). */
  persistClusters: () => Promise<void>;
  setClusterToRename: (clusterId: string | null) => void;
  /** Put back a board's saved clusters and clustering state (undo/redo of a group). */
  restoreClusters: (saved: AICluster[], clustering: { enabled: boolean; scope: 'board' | 'manual' }) => Promise<void>;
  /** Move items to new positions with the organize animation, and save them. */
  applyItemPositions: (positions: Map<string, { x: number; y: number }>) => Promise<void>;

  // Rescue stranded items
  rescueStrandedItems: () => number;
}

// Debounce helper for saveCanvasState (board-level saves)
let saveTimeout: ReturnType<typeof setTimeout> | null = null;

// Item-level saves are handled by the mutation queue (src/lib/mutationQueue.ts).
// Event listeners (beforeunload, visibilitychange) are registered in main.tsx.

// Deletes still writing to the database, so an undo can wait for them before
// putting the row back (otherwise the late delete removes the restored row).
const inFlightDeletes = new Map<string, Promise<void>>();

// An uploaded file outlives its card for a while, so undoing the delete brings
// back a working image instead of a broken one.
const STORAGE_REMOVAL_DELAY_MS = 10 * 60 * 1000;
const pendingStorageRemovals = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleStorageRemoval(itemId: string, storagePath: string) {
  const existing = pendingStorageRemovals.get(itemId);
  if (existing) clearTimeout(existing);
  pendingStorageRemovals.set(itemId, setTimeout(() => {
    pendingStorageRemovals.delete(itemId);
    supabase.storage.from('uploads').remove([storagePath])
      .catch((e) => console.error('Error deleting file from storage:', e));
  }, STORAGE_REMOVAL_DELAY_MS));
}

function cancelStorageRemoval(itemId: string) {
  const timer = pendingStorageRemovals.get(itemId);
  if (timer) clearTimeout(timer);
  pendingStorageRemovals.delete(itemId);
}

// Debounced board profile refresh — prevents excessive API calls
let profileRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
let profileNeedsRefreshAfterCurrent = false; // set when changes occur during an in-flight profile generation
const PROFILE_REFRESH_DELAY = 5000; // 5 seconds after last change

// Defined before useCanvasStore but only called from store actions (after store init).
// Uses lazy reference to avoid TDZ if bundler reorders declarations.
let _getCanvasStore: (() => CanvasState) | null = null;
function scheduleProfileRefresh() {
  if (profileRefreshTimeout) clearTimeout(profileRefreshTimeout);
  const getState = _getCanvasStore ?? useCanvasStore.getState;
  const state = getState();
  if (state.isProfilingBoard) {
    profileNeedsRefreshAfterCurrent = true;
    return;
  }
  profileRefreshTimeout = setTimeout(() => {
    const gs = _getCanvasStore ?? useCanvasStore.getState;
    const s = gs();
    if (s.currentBoardId && s.items.length > 0) {
      s.refreshBoardProfile();
    }
  }, PROFILE_REFRESH_DELAY);
}

// Get stored view mode with migration from old values
const getStoredViewMode = (): ViewMode => {
  const stored = localStorage.getItem('viewMode');
  // Migration from old view modes
  if (stored === 'wall') return 'discover';
  if (stored === 'canvas' || stored === 'boards' || stored === 'focus') return 'focus';
  if (stored === 'discover') return 'discover';
  return 'focus'; // Default to Focus
};

/** Returns true if the board has a meaningful saved viewport (not all-zero defaults). */
function hasValidSavedViewport(board: Board): boolean {
  return board.canvas_zoom > 0
    && !(board.canvas_position_x === 0 && board.canvas_position_y === 0 && board.canvas_zoom === 0);
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  // Initial state
  currentBoardId: null,
  boardPath: [],
  viewMode: getStoredViewMode(),
  panX: 0,
  panY: 0,
  zoom: 1,
  viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
  viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
  shouldFitToView: false,
  pendingFocusItemId: null,
  isAutoOrganizing: false,
  isBoardTransitioning: false,
  lastBoardNavAt: 0,
  activeTool: 'select',
  hoverPreviewEnabled: false,
  selectedItemIds: new Set(),
  newlyCreatedItemId: null,
  isDraggingFromToolbar: false,
  draggedToolType: null,
  dropTargetBoardId: null,
  boards: [],
  items: [],
  allItems: [],
  boardShares: [],
  isSidebarOpen: true,
  isDarkMode: localStorage.getItem('darkMode') === 'true',
  isGridVisible: localStorage.getItem('gridVisible') !== 'false', // Default to visible
  copiedItems: [],
  itemMinHeights: {},
  isLoading: true,
  allItemsLoaded: false,
  isSaving: false,
  isEditingItem: false,
  activeEditor: null,
  activeEditorItemId: null,
  wallScrollOffset: 0,
  boardProfile: null,
  isProfilingBoard: false,
  spatialClusters: [],
  isClusteringEnabled: false,
  clusterScope: 'board',
  isDetectingClusters: false,
  highlightedClusterId: null,
  clusterToRename: null,
  lastClusteredItemIds: [],
  
  // Computed helpers
  getBoundingBox: () => {
    const state = get();
    if (state.items.length === 0) return null;
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const item of state.items) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
      maxX = Math.max(maxX, item.position_x + item.width);
      maxY = Math.max(maxY, item.position_y + item.height);
    }
    
    return {
      minX,
      minY,
      maxX,
      maxY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      width: maxX - minX,
      height: maxY - minY,
    };
  },
  
  // Returns boards that have a corresponding board_link item on their parent
  // (filters out orphaned boards that shouldn't appear in UI listings)
  getVisibleBoards: () => {
    const { boards, allItems } = get();
    return boards.filter(b => {
      if (b.parent_id === null) return false; // Exclude home board
      return allItems.some(item =>
        item.item_type === 'board_link' &&
        item.board_id === b.parent_id &&
        (item.content as Record<string, unknown>)?.linked_board_id === b.id
      );
    });
  },

  // Navigation
  setCurrentBoard: (boardId) => set({ currentBoardId: boardId }),
  
  navigateToBoard: async (board) => {
    const state = get();

    // Block sticky creation IMMEDIATELY — before any async work
    // (saveCanvasState is a network call; a double-click can slip through the gap)
    set({ isBoardTransitioning: true, lastBoardNavAt: Date.now() });

    // Build the path from the board hierarchy — ancestors only, not the destination.
    // Walk up from the destination's parent to build [child-of-root, ..., parent].
    const newPath: Board[] = [];
    let ancestor = state.boards.find(b => b.id === board.parent_id);
    while (ancestor && ancestor.parent_id !== null) {
      newPath.unshift(ancestor);
      ancestor = state.boards.find(b => b.id === ancestor!.parent_id);
    }

    // Flush pending saves before clearing items to prevent content loss
    await (await import('@/lib/mutationQueue')).flush();

    // Save current canvas state before navigating
    await state.saveCanvasState();
    
    // Switch to focus mode when navigating to a board
    const newViewMode = 'focus';
    localStorage.setItem('viewMode', newViewMode);
    
    set({
      currentBoardId: board.id,
      boardPath: newPath,
      // Don't reset pan/zoom — the fit-to-view animation will smoothly transition
      // from current view to the new board's content (prevents jarring flash)
      selectedItemIds: new Set(),
      items: [],
      viewMode: newViewMode,
      shouldFitToView: true, // Trigger fit-to-view after items load
      isBoardTransitioning: true,
      lastBoardNavAt: Date.now(),
    });

    // Fetch items for the new board
    await get().fetchItems(board.id);
    set({ isBoardTransitioning: false });

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('board', board.id);
    url.searchParams.delete('view');
    window.history.pushState({}, '', url.toString());
  },
  
  openItemOnCanvas: async (item) => {
    const board = get().boards.find(b => b.id === item.board_id);
    if (!board) return false;
    set({ pendingFocusItemId: item.id });
    await get().navigateToBoard(board);
    return true;
  },

  goToFocusLanding: async () => {
    const state = get();

    // Block sticky creation IMMEDIATELY — before any async work
    set({ isBoardTransitioning: true, lastBoardNavAt: Date.now() });

    // Flush pending saves before clearing items to prevent content loss
    await (await import('@/lib/mutationQueue')).flush();

    // Save current canvas state before leaving
    if (state.currentBoardId) {
      await state.saveCanvasState();
    }
    
    // Ensure boards are loaded (use fresh state after saveCanvasState)
    let boards = get().boards;
    if (boards.length === 0) {
      await get().fetchBoards();
      boards = get().boards;
    }

    // Navigate to home board (the board with no parent)
    const homeBoard = boards.find(b => b.parent_id === null);
    if (homeBoard) {
      const restoreViewport = hasValidSavedViewport(homeBoard);
      set({
        currentBoardId: homeBoard.id,
        boardPath: [],
        selectedItemIds: new Set(),
        items: [],
        viewMode: 'focus',
        ...(restoreViewport
          ? {
              panX: homeBoard.canvas_position_x,
              panY: homeBoard.canvas_position_y,
              zoom: homeBoard.canvas_zoom,
              shouldFitToView: false,
            }
          : { shouldFitToView: true }),
        isBoardTransitioning: true,
        lastBoardNavAt: Date.now(),
      });

      // Fetch items for home board
      await get().fetchItems(homeBoard.id);
      set({ isBoardTransitioning: false });

      // Update URL — remove board param (home is default), remove view param
      const url = new URL(window.location.href);
      url.searchParams.delete('board');
      url.searchParams.delete('view');
      window.history.pushState({}, '', url.toString());
    } else {
      console.error('No home board found');
      set({ isBoardTransitioning: false });
    }
  },
  
  navigateBack: async (toBoardId) => {
    const state = get();

    // Block sticky creation IMMEDIATELY — before any async work
    set({ isBoardTransitioning: true, lastBoardNavAt: Date.now() });

    // Flush pending saves before clearing items to prevent content loss
    await (await import('@/lib/mutationQueue')).flush();

    // Save current canvas state before navigating
    await state.saveCanvasState();
    
    if (toBoardId === null) {
      // Going to root (Home)
      const freshBoards = get().boards;
      const homeBoard = freshBoards.find(b => b.parent_id === null);
      if (homeBoard) {
        const restoreViewport = hasValidSavedViewport(homeBoard);
        set({
          currentBoardId: homeBoard.id,
          boardPath: [],
          selectedItemIds: new Set(),
          items: [],
          ...(restoreViewport
            ? {
                panX: homeBoard.canvas_position_x,
                panY: homeBoard.canvas_position_y,
                zoom: homeBoard.canvas_zoom,
                shouldFitToView: false,
              }
            : { shouldFitToView: true }),
          isBoardTransitioning: true,
          lastBoardNavAt: Date.now(),
        });
        await get().fetchItems(homeBoard.id);
        set({ isBoardTransitioning: false });
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('board');
      window.history.pushState({}, '', url.toString());
    } else {
      // Find the board and truncate path
      const freshBoards = get().boards;
      const targetBoard = freshBoards.find(b => b.id === toBoardId);
      if (targetBoard) {
        const pathIndex = state.boardPath.findIndex(b => b.id === toBoardId);
        // Slice up to (but not including) the target — it becomes the current board, not an ancestor
        const newPath = pathIndex >= 0 ? state.boardPath.slice(0, pathIndex) : [];

        const restoreViewport = hasValidSavedViewport(targetBoard);
        set({
          currentBoardId: toBoardId,
          boardPath: newPath,
          selectedItemIds: new Set(),
          items: [],
          ...(restoreViewport
            ? {
                panX: targetBoard.canvas_position_x,
                panY: targetBoard.canvas_position_y,
                zoom: targetBoard.canvas_zoom,
                shouldFitToView: false,
              }
            : { shouldFitToView: true }),
          isBoardTransitioning: true,
          lastBoardNavAt: Date.now(),
        });
        await get().fetchItems(toBoardId);
        set({ isBoardTransitioning: false });

        const url = new URL(window.location.href);
        url.searchParams.set('board', toBoardId);
        window.history.pushState({}, '', url.toString());
      }
    }
  },
  
  setViewMode: (mode) => {
    localStorage.setItem('viewMode', mode);
    set({ viewMode: mode });
    
    // Update URL
    const url = new URL(window.location.href);
    if (mode === 'focus') {
      url.searchParams.delete('view');
    } else {
      url.searchParams.set('view', mode);
    }
    window.history.pushState({}, '', url.toString());
    
    // Fetch all items when switching to Discover mode
    if (mode === 'discover') {
      get().fetchAllItems();
    }
    // Navigate to home board when switching to Create/Focus mode
    if (mode === 'focus') {
      const state = get();
      const homeBoard = state.boards.find(b => b.parent_id === null);
      if (homeBoard && state.currentBoardId !== homeBoard.id) {
        // Navigate to home board
        state.navigateToBoard(homeBoard);
      } else if (!homeBoard) {
        // Boards not loaded yet, fetch them first
        get().fetchBoards().then(() => {
          const newState = get();
          const home = newState.boards.find(b => b.parent_id === null);
          if (home) {
            newState.navigateToBoard(home);
          }
        });
      }
    }
  },
  
  setPan: (x, y) => {
    const state = get();
    const { zoom, viewportWidth, viewportHeight } = state;
    const bbox = state.getBoundingBox();

    let clampedX = x;
    let clampedY = y;

    if (viewportWidth > 0 && viewportHeight > 0) {
      const FIXED_MARGIN = 250; // generous canvas-unit margin around content

      // How many canvas units the viewport shows at the current zoom
      const viewW = viewportWidth / zoom;
      const viewH = viewportHeight / zoom;

      // Use the LARGER of item bounds or minimum canvas bounds
      // This prevents bounds from contracting when items move, causing viewport jumps
      const effectiveBounds = {
        minX: bbox ? Math.min(bbox.minX, CANVAS_MIN_BOUNDS.minX) : CANVAS_MIN_BOUNDS.minX,
        minY: bbox ? Math.min(bbox.minY, CANVAS_MIN_BOUNDS.minY) : CANVAS_MIN_BOUNDS.minY,
        maxX: bbox ? Math.max(bbox.maxX, CANVAS_MIN_BOUNDS.maxX) : CANVAS_MIN_BOUNDS.maxX,
        maxY: bbox ? Math.max(bbox.maxY, CANVAS_MIN_BOUNDS.maxY) : CANVAS_MIN_BOUNDS.maxY,
      };
      
      // Ensure effective minY respects navbar zone
      effectiveBounds.minY = Math.max(effectiveBounds.minY, MIN_ITEM_Y);

      // Extra space beyond content that the viewport can see
      const contentWidth = effectiveBounds.maxX - effectiveBounds.minX;
      const contentHeight = effectiveBounds.maxY - effectiveBounds.minY;
      const extraW = Math.max(0, viewW - contentWidth);
      const extraH = Math.max(0, viewH - contentHeight);

      // Dynamic margin: grows as you zoom out so content can be centered
      const dynamicMarginX = Math.max(FIXED_MARGIN, extraW / 2);
      const dynamicMarginY = Math.max(FIXED_MARGIN, extraH / 2);

      // Maximum pan = content's left/top edge sits at the dynamic margin from viewport edge
      let maxPanX = -effectiveBounds.minX + dynamicMarginX;
      let maxPanY = -effectiveBounds.minY + dynamicMarginY;

      // Minimum pan = content's right/bottom edge sits at the dynamic margin from viewport edge
      let minPanX = -(effectiveBounds.maxX) + viewW - dynamicMarginX;
      let minPanY = -(effectiveBounds.maxY) + viewH - dynamicMarginY;

      // Guarantee minimum scroll range so zoomed-out panning isn't locked.
      // Without this, zooming out far causes maxPan ≈ minPan, freezing pan entirely.
      const MIN_SCROLL_FRACTION = 0.6;
      const minScrollRangeX = viewW * MIN_SCROLL_FRACTION;
      const minScrollRangeY = viewH * MIN_SCROLL_FRACTION;

      if (maxPanX - minPanX < minScrollRangeX) {
        const cx = (maxPanX + minPanX) / 2;
        maxPanX = cx + minScrollRangeX / 2;
        minPanX = cx - minScrollRangeX / 2;
      }
      if (maxPanY - minPanY < minScrollRangeY) {
        const cy = (maxPanY + minPanY) / 2;
        maxPanY = cy + minScrollRangeY / 2;
        minPanY = cy - minScrollRangeY / 2;
      }

      clampedX = Math.max(minPanX, Math.min(maxPanX, x));
      clampedY = Math.max(minPanY, Math.min(maxPanY, y));
    }

    set({ panX: clampedX, panY: clampedY });
    // Debounced save
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => get().saveCanvasState(), 500);
  },
  
  setViewportSize: (width, height) => {
    set({ viewportWidth: width, viewportHeight: height });
  },
  
  setZoom: (zoom) => {
    // Allow slight overshoot for bounce animation (0.08-4.1), will be corrected
    const clampedZoom = Math.max(0.08, Math.min(4.1, zoom));
    set({ zoom: clampedZoom });
    // Debounced save
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => get().saveCanvasState(), 500);
  },
  
  setActiveTool: (tool) => set({ activeTool: tool }),
  
  toggleHoverPreview: () => set((state) => ({ hoverPreviewEnabled: !state.hoverPreviewEnabled })),
  
  selectItem: (itemId, additive = false) => {
    const state = get();
    
    // If additive mode (shift+click), toggle selection
    if (additive) {
      const newSelection = new Set(state.selectedItemIds);
      if (newSelection.has(itemId)) {
        newSelection.delete(itemId);
      } else {
        newSelection.add(itemId);
      }
      set({ selectedItemIds: newSelection });
    } else {
      // Non-additive: select only this item (don't toggle if already selected)
      set({ selectedItemIds: new Set([itemId]) });
    }
  },
  
  clearSelection: () => set({ selectedItemIds: new Set() }),
  
  setSelectedItems: (itemIds: Set<string>) => set({ selectedItemIds: itemIds }),
  
  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
  
  toggleDarkMode: () => {
    const newMode = !get().isDarkMode;
    localStorage.setItem('darkMode', String(newMode));
    
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    set({ isDarkMode: newMode });
  },
  
  toggleGrid: () => {
    const newValue = !get().isGridVisible;
    localStorage.setItem('gridVisible', String(newValue));
    set({ isGridVisible: newValue });
  },
  
  setIsEditingItem: (isEditing) => set({ isEditingItem: isEditing }),
  
  setActiveEditor: (editor, itemId = null) => set({ activeEditor: editor, activeEditorItemId: editor ? itemId : null }),
  
  setWallScrollOffset: (offset) => set({ wallScrollOffset: offset }),
  
  setDraggingFromToolbar: (isDragging, toolType = null) => set({ 
    isDraggingFromToolbar: isDragging, 
    draggedToolType: toolType 
  }),
  
  setDropTargetBoardId: (boardId) => set({ dropTargetBoardId: boardId }),

  setItemMinHeight: (itemId, minHeight) =>
    set((state) => {
      const current = state.itemMinHeights[itemId] ?? 0;
      // Avoid churn from tiny fluctuations
      if (Math.abs(current - minHeight) < 6) return {};
      return {
        itemMinHeights: {
          ...state.itemMinHeights,
          [itemId]: minHeight,
        },
      };
    }),
  
  clearShouldFitToView: () => set({ shouldFitToView: false }),
  clearPendingFocusItem: () => set({ pendingFocusItemId: null }),
  
  pullItemToCurrentBoard: async (itemId, position) => {
    const state = get();
    const boardId = state.currentBoardId;
    const source = state.allItems.find(i => i.id === itemId);
    if (!boardId || !source || source.board_id === boardId) return null;

    const z_index = state.items.reduce((max, i) => Math.max(max, i.z_index), 0) + 1;
    const position_x = Math.round(position.x);
    const position_y = Math.round(position.y);
    const moved: Item = { ...source, board_id: boardId, position_x, position_y, z_index, updated_at: new Date().toISOString() };

    // Optimistic: the card lands where it was dropped and leaves the folder now
    set({
      items: [...state.items.filter(i => i.id !== itemId), moved],
      allItems: state.allItems.map(i => (i.id === itemId ? moved : i)),
      selectedItemIds: new Set([itemId]),
    });

    const { error } = await supabase
      .from('items')
      .update({ board_id: boardId, position_x, position_y, z_index })
      .eq('id', itemId);

    if (error) {
      console.error('Failed to move item out of folder:', error);
      set(s => ({
        items: s.items.filter(i => i.id !== itemId),
        allItems: s.allItems.map(i => (i.id === itemId ? source : i)),
        selectedItemIds: new Set(),
      }));
      toast.error('Could not move the item out of the folder');
      return null;
    }

    scheduleProfileRefresh();
    return moved;
  },

  moveItemsToBoard: async (itemIds, targetBoardId) => {
    const state = get();

    const isDebug = (() => {
      try {
        return new URLSearchParams(window.location.search).has('debugDragToBoard');
      } catch {
        return false;
      }
    })();

    console.log('=== moveItemsToBoard called ===');
    console.log('Target board ID:', targetBoardId);
    console.log('Item IDs to move:', itemIds);

    // Validate inputs
    if (!targetBoardId) {
      if (isDebug) alert('DEBUG: targetBoardId is empty!');
      console.error('targetBoardId is empty');
      toast.error('Target board not found');
      return;
    }

    if (!itemIds || itemIds.length === 0) {
      if (isDebug) alert('DEBUG: No items to move!');
      console.error('itemIds is empty');
      return;
    }

    if (isDebug) alert(`DEBUG: Moving ${itemIds.length} items to board ${targetBoardId}`);

    const targetBoard = state.boards.find(b => b.id === targetBoardId);
    if (!targetBoard) {
      if (isDebug) alert('DEBUG: Target board not found in local boards list!');
      console.error('ERROR: Board not found in boards list:', targetBoardId);
      toast.error('Target board not found');
      return;
    }

    try {
      // Update database
      const { error } = await supabase
        .from('items')
        .update({ board_id: targetBoardId })
        .in('id', itemIds);

      if (error) {
        if (isDebug) alert('DEBUG: Database error - ' + error.message);
        console.error('Database error:', error);
        toast.error('Failed to move items: ' + error.message);
        return;
      }

      if (isDebug) alert('DEBUG: Database updated! Removing items from view...');

      // Remove items from local state (current board view)
      const beforeCount = state.items.length;
      const nextItems = state.items.filter(i => !itemIds.includes(i.id));
      console.log('Items before:', beforeCount, 'Items after:', nextItems.length);

      set({
        items: nextItems,
        allItems: state.allItems.map(i => (itemIds.includes(i.id) ? { ...i, board_id: targetBoardId } : i)),
        selectedItemIds: new Set(),
      });

      toast.success(`Moved ${itemIds.length} item${itemIds.length > 1 ? 's' : ''} to ${targetBoard.name}`);
      
      // Schedule board profile refresh after items moved
      scheduleProfileRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (isDebug) alert('DEBUG: Unexpected error - ' + message);
      console.error('Unexpected error moving items:', err);
      toast.error('Failed to move items');
    }
  },
  
  // Data actions
  fetchBoards: async () => {
    const { data, error } = await supabase
      .from('boards')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error('Error fetching boards:', error);
      toast.error('Failed to load boards. Please refresh the page.');
      return;
    }
    
    set({ boards: data || [] });
  },

  /**
   * One-time repair: find child boards under the home board that have no
   * corresponding board_link item, and create the missing links so they
   * become visible on the home canvas.
   */
  repairMissingBoardLinks: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const state = get();
    const homeBoard = state.boards.find(b => b.parent_id === null);
    if (!homeBoard) return;

    // Find child boards of the home board
    const childBoards = state.boards.filter(b => b.parent_id === homeBoard.id);
    if (childBoards.length === 0) return;

    // Fetch all items on the home board to check for existing board_links
    const { data: homeItems } = await supabase
      .from('items')
      .select('content')
      .eq('board_id', homeBoard.id)
      .eq('item_type', 'board_link');

    const existingLinkedIds = new Set(
      (homeItems || []).map(
        (item) => (item.content as Record<string, unknown>)?.linked_board_id as string
      ).filter(Boolean)
    );

    // Find child boards with no board_link
    const missingBoards = childBoards.filter(b => !existingLinkedIds.has(b.id));
    if (missingBoards.length === 0) return;

    // Count existing board_links for grid positioning
    const existingCount = existingLinkedIds.size;

    for (let i = 0; i < missingBoards.length; i++) {
      const board = missingBoards[i];
      const gridIndex = existingCount + i;
      const { error } = await supabase.from('items').insert({
        user_id: user.id,
        board_id: homeBoard.id,
        item_type: 'board_link',
        content: { linked_board_id: board.id, name: board.name, color: board.icon_color },
        position_x: 100 + (gridIndex % 3) * 260,
        position_y: 100 + Math.floor(gridIndex / 3) * 220,
        width: 200,
        height: 150,
      });
      if (error) {
        console.error('[repairMissingBoardLinks] Failed for board', board.name, error);
      }
    }

    console.log(`[repairMissingBoardLinks] Created ${missingBoards.length} missing board link(s)`);

    // Refresh items if we're currently viewing the home board
    if (state.currentBoardId === homeBoard.id) {
      await get().fetchItems(homeBoard.id);
    }
  },

  fetchItems: async (boardId) => {
    set({ isLoading: true });
    // What was on the board when the fetch started: only those can have been deleted since
    const loadedBefore = new Set(get().items.map(item => item.id));
    
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .eq('board_id', boardId)
      .order('z_index', { ascending: true });
    
    if (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load items. Please try again.');
      set({ isLoading: false });
      return;
    }
    
    const typedItems = (data || []).map(item => ({
      ...item,
      item_type: item.item_type as ItemType,
    }));
    
    // Drop selected ids that are no longer on the board (an undo just deleted
    // them, say), so the selection count and pills stop counting them. A card
    // made and selected while this fetch ran is not in the response yet: keep it.
    const onBoard = new Set(typedItems.map(item => item.id));
    const { selectedItemIds } = get();
    const keptSelection = [...selectedItemIds].filter(id => onBoard.has(id) || !loadedBefore.has(id));
    set({
      items: typedItems,
      isLoading: false,
      boardProfile: null,
      ...(keptSelection.length !== selectedItemIds.size ? { selectedItemIds: new Set(keptSelection) } : {}),
    });
    
    // Trigger background board profiling after items load
    setTimeout(() => get().refreshBoardProfile(), 2000);
  },
  
  fetchAllItems: async () => {
    // NOTE: This function populates allItems (used for counts in sidebar/board cards).
    // It must NOT touch isLoading — that flag is owned by fetchItems() and controls
    // whether InfiniteCanvas shows items vs loading state. Sharing it causes a race
    // condition that blanks the canvas on initial load.

    // Get current user to ensure we only fetch their items
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('No user logged in, skipping fetchAllItems');
      set({ allItems: [], allItemsLoaded: true });
      return;
    }

    // CRITICAL: Explicitly filter by user_id to prevent data leakage
    // RLS should handle this, but belt-and-suspenders approach for security
    // The API caps every request at 1000 rows whatever .limit() says, so page
    // through. id breaks created_at ties (a bulk import shares one timestamp
    // per few hundred rows): pages never overlap, and the order is the same
    // on every fetch, so a refetch after a delete does not reshuffle the Wall.
    const { data, error } = await fetchAllPages(
      (from, to, withCount) =>
        supabase
          .from('items')
          .select('*', withCount ? { count: 'exact' } : undefined)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, to),
      (row) => row.id,
    );

    if (error) {
      console.error('Error fetching all items:', error);
      toast.error('Failed to load items. Please try again.');
      set({ allItemsLoaded: true });
      return;
    }

    const typedItems = (data || []).map(item => ({
      ...item,
      item_type: item.item_type as ItemType,
    }));

    set({ allItems: typedItems, allItemsLoaded: true });
  },
  
  fetchBoardShares: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      set({ boardShares: [] });
      return;
    }
    
    // Get shares where user is the sharer (their boards that are shared)
    const { data, error } = await supabase
      .from('board_shares')
      .select('*')
      .eq('shared_by', user.id)
      .eq('is_active', true);
    
    if (error) {
      console.error('Error fetching board shares:', error);
      set({ boardShares: [] });
      return;
    }
    
    set({ boardShares: (data || []) as BoardShare[] });
  },
  
  createBoard: async (name, parentId, position, options) => {
    const state = get();
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to create boards.');
      return null;
    }
    
    // Generate a random pastel color for the new board
    const { getRandomFolderColor } = await import('@/lib/folderColors');
    const iconColor = options?.iconColor || getRandomFolderColor().id;
    
    const { data, error } = await supabase
      .from('boards')
      .insert({ ...(options?.id ? { id: options.id } : {}), name, parent_id: parentId, user_id: user.id, icon_color: iconColor })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating board:', error);
      toast.error('Failed to create board. Please try again.');
      return null;
    }
    
    if (position && state.currentBoardId) {
      await get().createItem({
        board_id: state.currentBoardId,
        item_type: 'board_link',
        position_x: position.x,
        position_y: position.y,
        width: 200,
        height: 150,
        content: { linked_board_id: data.id, name: data.name, color: data.icon_color },
      });
    }
    
    set({ boards: [...state.boards, data] });
    return data;
  },
  
  updateBoard: async (id, updates) => {
    const state = get();
    
    // Update local state immediately
    set({
      boards: state.boards.map(b => b.id === id ? { ...b, ...updates } : b),
    });
    
    // Also update any board_link items that reference this board
    if (updates.name || updates.icon_color) {
      const boardLinkItems = state.items.filter(i => {
        if (i.item_type !== 'board_link') return false;
        const content = i.content as { linked_board_id?: string };
        return content.linked_board_id === id;
      });
      
      for (const item of boardLinkItems) {
        const content = item.content as { linked_board_id?: string; name?: string; color?: string };
        const newContent = { ...content };
        if (updates.name) newContent.name = updates.name;
        if (updates.icon_color) newContent.color = updates.icon_color;
        
        set({
          items: state.items.map(i => i.id === item.id ? { ...i, content: newContent } : i),
          allItems: state.allItems.map(i => i.id === item.id ? { ...i, content: newContent } : i),
        });
        
        // Update board_link item in database
        await supabase.from('items').update({ content: newContent }).eq('id', item.id);
      }
    }
    
    // Update in database
    const { error } = await supabase.from('boards').update(updates).eq('id', id);
    if (error) {
      console.error('Error updating board:', error);
      toast.error('Failed to update board');
    }
  },
  
  deleteBoard: async (id) => {
    const state = get();
    
    // Find the board_link item that references this board
    const boardLinkItem = state.items.find(i => {
      if (i.item_type !== 'board_link') return false;
      const content = i.content as { linked_board_id?: string };
      return content.linked_board_id === id;
    });
    
    // Delete the board_link item first (if found)
    if (boardLinkItem) {
      set({
        items: state.items.filter(i => i.id !== boardLinkItem.id),
        allItems: state.allItems.filter(i => i.id !== boardLinkItem.id),
      });
      await supabase.from('items').delete().eq('id', boardLinkItem.id);
    }
    
    // Delete all items in the board
    const { error: itemsError } = await supabase.from('items').delete().eq('board_id', id);
    if (itemsError) {
      console.error('Error deleting board items:', itemsError);
    }
    
    // Delete child boards recursively
    const childBoards = state.boards.filter(b => b.parent_id === id);
    for (const childBoard of childBoards) {
      await get().deleteBoard(childBoard.id);
    }
    
    // Delete the board itself
    const { error } = await supabase.from('boards').delete().eq('id', id);
    if (error) {
      console.error('Error deleting board:', error);
      toast.error('Failed to delete board');
      return;
    }
    
    // Remove from local state
    set({
      boards: state.boards.filter(b => b.id !== id),
    });
    
  },

  createItem: async (item) => {
    const state = get();
    // Determine board_id: use provided value, fall back to currentBoardId, or null for Wall items
    const boardId = item.board_id !== undefined ? item.board_id : state.currentBoardId;
    // Items created for another board (e.g. from Discover) must not land in the open canvas
    const isOnCurrentBoard = boardId === state.currentBoardId;
    const siblings = isOnCurrentBoard ? state.items : state.allItems.filter(i => i.board_id === boardId);
    const maxZIndex = siblings.reduce((max, i) => Math.max(max, i.z_index), 0);
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to create items.');
      return null;
    }
    
    const { data, error } = await supabase
      .from('items')
      .insert({
        board_id: boardId, // Can be null for items created in Wall view
        item_type: item.item_type!,
        position_x: item.position_x || 0,
        position_y: item.position_y || 0,
        width: item.width || 200,
        height: item.height || 150,
        z_index: maxZIndex + 1,
        content: (item.content || {}) as Json,
        user_id: user.id,
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating item:', error);
      toast.error('Failed to save item. Please try again.');
      return null;
    }
    
    const typedItem = { ...data, item_type: data.item_type as ItemType };
    
    // Also add to allItems for Wall sync
    const currentAllItems = get().allItems;
    
    set(isOnCurrentBoard
      ? {
          // get(), not the pre-await snapshot: ghosts and edits may have changed items meanwhile
          items: [...get().items, typedItem],
          allItems: [typedItem, ...currentAllItems],
          selectedItemIds: new Set([data.id]),
          newlyCreatedItemId: data.id,
        }
      : { allItems: [typedItem, ...currentAllItems] });
    
    // Schedule board profile refresh after item creation
    scheduleProfileRefresh();

    // Generate embedding in background (fire-and-forget)
    const textForEmbedding = extractItemText({ item_type: typedItem.item_type, content: typedItem.content as Record<string, unknown> });
    if (textForEmbedding.length >= 5) {
      supabase.functions.invoke('generate-embedding', {
        body: { items: [{ itemId: typedItem.id, content: textForEmbedding }] },
      }).catch(e => console.warn('[Embeddings] Background generation failed:', e));
    }
    // Subject labels for the mobile Library (batched, fire-and-forget)
    queueSubjectTagging([typedItem]);

    return typedItem;
  },
  
  createItems: async (inputs, options = {}) => {
    if (inputs.length === 0) return [];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to create items.');
      return [];
    }

    const state = get();
    // z order continues per board, so a batch stacks in the order given
    const nextZ = new Map<string | null, number>();
    const rows = inputs.map((item) => {
      const boardId = item.board_id !== undefined ? item.board_id : state.currentBoardId;
      if (!nextZ.has(boardId)) {
        const siblings = boardId === state.currentBoardId ? state.items : state.allItems.filter(i => i.board_id === boardId);
        nextZ.set(boardId, siblings.reduce((max, i) => Math.max(max, i.z_index), 0));
      }
      const z = nextZ.get(boardId)! + 1;
      nextZ.set(boardId, z);
      return {
        board_id: boardId,
        item_type: item.item_type!,
        position_x: item.position_x || 0,
        position_y: item.position_y || 0,
        width: item.width || 200,
        height: item.height || 150,
        z_index: item.z_index ?? z,
        content: (item.content || {}) as Json,
        user_id: user.id,
      };
    });

    const { data, error } = await supabase.from('items').insert(rows).select();
    if (error || !data) {
      console.error('Error creating items:', error);
      toast.error('Failed to save items. Please try again.');
      return [];
    }

    const created = data.map(row => ({ ...row, item_type: row.item_type as ItemType }));
    const currentBoardId = get().currentBoardId;
    const onBoard = created.filter(i => i.board_id === currentBoardId);
    set({
      // get(), not the pre-await snapshot: ghosts and edits may have changed items meanwhile
      items: onBoard.length ? [...get().items, ...onBoard] : get().items,
      allItems: [...created, ...get().allItems],
      ...(onBoard.length ? { selectedItemIds: new Set(onBoard.map(i => i.id)) } : {}),
    });

    scheduleProfileRefresh();
    if (options.history !== false) {
      const group = typeof options.history === 'object' ? options.history : undefined;
      useHistoryStore.getState().pushAction({ type: 'CREATE_ITEM', timestamp: Date.now(), items: created }, group);
    }

    // One embedding call for the batch (fire-and-forget)
    const embeddingItems = created
      .map(item => ({ itemId: item.id, content: extractItemText({ item_type: item.item_type, content: item.content as Record<string, unknown> }) }))
      .filter(item => item.content.length >= 5);
    if (embeddingItems.length > 0) {
      supabase.functions.invoke('generate-embedding', { body: { items: embeddingItems } })
        .catch(e => console.warn('[Embeddings] Background generation failed:', e));
    }
    queueSubjectTagging(created);
    return created;
  },

  clearNewlyCreatedItem: () => set({ newlyCreatedItemId: null }),
  
  updateItem: async (id, updates) => {
    const state = get();

    // Merge content with existing item to prevent partial overwrites
    // (e.g., TextCard writing { body } must not erase { title, partnr_generated })
    if (updates.content) {
      const existingItem = state.items.find(i => i.id === id) || state.allItems.find(i => i.id === id);
      if (existingItem && typeof existingItem.content === 'object' && existingItem.content !== null) {
        updates = {
          ...updates,
          content: { ...(existingItem.content as Record<string, Json | undefined>), ...(updates.content as Record<string, Json | undefined>) } as Json,
        };
      }
      // A link's pending preview just landed: tag it now that there is something to see
      if (existingItem && isAwaitingPreview(existingItem) && !isAwaitingPreview({ item_type: existingItem.item_type, content: updates.content })) {
        queueSubjectTagging([{ ...existingItem, content: updates.content }]);
      } else if (existingItem && existingItem.item_type === 'link' && !(existingItem.content as Record<string, unknown>)?.image && (updates.content as Record<string, unknown>)?.image) {
        // A picture arrived for a link saved without one (preview backfill): tag it
        // again from the picture, since it was tagged from its bare title before
        queueSubjectTagging([{ ...existingItem, content: updates.content }]);
      }
    }

    // Update local state immediately for responsiveness (both items and allItems)
    set({
      items: state.items.map(item => item.id === id ? { ...item, ...updates } : item),
      allItems: state.allItems.map(item => item.id === id ? { ...item, ...updates } : item),
    });

    // Enqueue mutation for debounced persistence via mutation queue
    const { enqueueMutation } = await import('@/lib/mutationQueue');
    const isPositionOnly = ('position_x' in updates || 'position_y' in updates) && !('content' in updates);
    enqueueMutation(id, updates as Record<string, unknown>, isPositionOnly ? 'position' : 'content');
  },
  
  deleteItem: async (id) => {
    const state = get();
    const item = state.items.find(i => i.id === id) || state.allItems.find(i => i.id === id);
    
    // Delete from local state immediately (both items and allItems)
    set({
      items: state.items.filter(i => i.id !== id),
      allItems: state.allItems.filter(i => i.id !== id),
      selectedItemIds: new Set([...state.selectedItemIds].filter(itemId => itemId !== id)),
    });
    
    // Schedule board profile refresh after deletion
    scheduleProfileRefresh();

    const run = async () => {
      // Handle board_link deletion
      if (item?.item_type === 'board_link') {
        const content = item.content as { linked_board_id?: string };
        if (content.linked_board_id) {
          const { error } = await supabase.from('boards').delete().eq('id', content.linked_board_id);
          if (error) {
            console.error('Error deleting linked board:', error);
          }
          set({ boards: get().boards.filter(b => b.id !== content.linked_board_id) });
        }
      }

      // Handle file/image deletion - remove from storage once undo is unlikely
      if (item?.item_type === 'image' || item?.item_type === 'file') {
        const content = item.content as { url?: string };
        const urlParts = content.url?.split('/uploads/') ?? [];
        if (urlParts.length > 1) scheduleStorageRemoval(id, urlParts[1]);
      }

      // Delete from database
      const { error } = await supabase.from('items').delete().eq('id', id);
      if (error) {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item. Please try again.');
      }
    };

    const pending = run().finally(() => {
      if (inFlightDeletes.get(id) === pending) inFlightDeletes.delete(id);
    });
    inFlightDeletes.set(id, pending);
    await pending;
  },
  
  deleteItems: async (ids) => {
    for (const id of ids) await get().deleteItem(id);
  },

  restoreItems: async (items) => {
    if (items.length === 0) return;
    const ids = new Set(items.map(i => i.id));
    await Promise.all(items.map(i => inFlightDeletes.get(i.id)).filter(Boolean));
    items.forEach(i => cancelStorageRemoval(i.id));

    const { error } = await supabase.from('items').insert(items.map(item => ({
      id: item.id,
      board_id: item.board_id,
      item_type: item.item_type,
      position_x: item.position_x,
      position_y: item.position_y,
      width: item.width,
      height: item.height,
      z_index: item.z_index,
      content: item.content as Json,
      user_id: item.user_id,
    })));
    if (error) throw error;

    const clean = items.map(({ isGhost: _g, streamProgress: _p, ghostColor: _c, ...rest }) => rest as Item);
    set(s => ({
      items: [
        ...s.items.filter(i => !ids.has(i.id)),
        ...clean.filter(i => i.board_id === s.currentBoardId),
      ].sort((a, b) => a.z_index - b.z_index),
      allItems: [...s.allItems.filter(i => !ids.has(i.id)), ...clean],
    }));
    scheduleProfileRefresh();
  },
  
  saveCanvasState: async () => {
    const state = get();
    if (!state.currentBoardId) return;
    
    const { error } = await supabase.from('boards').update({
      canvas_position_x: state.panX,
      canvas_position_y: state.panY,
      canvas_zoom: state.zoom,
    }).eq('id', state.currentBoardId);
    
    if (error) {
      console.error('Error saving canvas state:', error);
    } else {
      // Keep in-memory boards in sync so navigateBack can restore viewport
      set({
        boards: state.boards.map(b =>
          b.id === state.currentBoardId
            ? { ...b, canvas_position_x: state.panX, canvas_position_y: state.panY, canvas_zoom: state.zoom }
            : b
        ),
      });
    }
  },

  // Reset store on logout to prevent data leakage between users
  // Ghost item management (Phase 4)
  createGhost: (ghost) => {
    const state = get();
    const ghostItem: Item = {
      id: ghost.id,
      board_id: state.currentBoardId || '',
      item_type: ghost.itemType,
      position_x: ghost.x,
      position_y: ghost.y,
      width: ghost.width,
      height: ghost.height,
      z_index: state.items.reduce((max, i) => Math.max(max, i.z_index), 0) + 1,
      content: {} as Json,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      isGhost: true,
      streamProgress: 0,
      ghostColor: ghost.ghostColor,
    };
    set({ items: [...state.items, ghostItem] });
  },

  hydrateGhost: (ghostId, realData) => {
    set(state => {
      // createItem has usually added the real item already. Keep that full row and
      // drop the ghost, or the board holds two entries with the same id.
      if (state.items.some(item => item.id === realData.id)) {
        return {
          items: state.items
            .filter(item => item.id !== ghostId)
            .map(item => item.id === realData.id ? { ...item, streamProgress: 1 } : item),
        };
      }
      return {
        items: state.items.map(item =>
          item.id === ghostId
            ? { ...item, id: realData.id, content: realData.content as Json, isGhost: false, streamProgress: 1 }
            : item
        ),
      };
    });
  },

  updateGhostProgress: (ghostId, progress) => {
    set(state => ({
      items: state.items.map(item =>
        item.id === ghostId
          ? { ...item, streamProgress: progress }
          : item
      ),
    }));
  },

  removeGhost: (ghostId) => {
    set(state => ({
      items: state.items.filter(item => item.id !== ghostId),
    }));
  },

  resetStore: () => {
    set({
      currentBoardId: null,
      boardPath: [],
      boards: [],
      items: [],
      allItems: [],
      boardShares: [],
      selectedItemIds: new Set(),
      newlyCreatedItemId: null,
      panX: 0,
      panY: 0,
      zoom: 1,
      itemMinHeights: {},
      copiedItems: [],
      isLoading: true,
    });
  },
  
  // Copy selected items to clipboard
  copySelectedItems: () => {
    const state = get();
    const selectedIds = [...state.selectedItemIds];
    if (selectedIds.length === 0) return;
    
    // Get all selected items (excluding board_link since those can't be duplicated easily)
    const itemsToCopy = state.items.filter(
      item => selectedIds.includes(item.id) && item.item_type !== 'board_link'
    );
    
    if (itemsToCopy.length > 0) {
      set({ copiedItems: itemsToCopy });
      toast.success(`Copied ${itemsToCopy.length} item${itemsToCopy.length > 1 ? 's' : ''}`);
    }
  },
  
  // Paste copied items at position
  pasteItems: async (position) => {
    const state = get();
    if (state.copiedItems.length === 0 || !state.currentBoardId) return [];
    
    // Calculate the bounding box of copied items to center the paste
    let minX = Infinity, minY = Infinity;
    for (const item of state.copiedItems) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
    }
    
    // Offset to place items at paste position
    const offsetX = position.x - minX;
    const offsetY = position.y - minY;
    
    const createdItems: Item[] = [];
    
    for (const item of state.copiedItems) {
      // Clone content (deep copy for safety)
      const content = typeof item.content === 'object' && item.content !== null
        ? JSON.parse(JSON.stringify(item.content))
        : {};
      
      // Create item at new position
      const newItem = await get().createItem({
        board_id: state.currentBoardId,
        item_type: item.item_type,
        position_x: Math.round((item.position_x + offsetX) / 20) * 20,
        position_y: Math.round((item.position_y + offsetY) / 20) * 20,
        width: item.width,
        height: item.height,
        content,
      });
      
      if (newItem) {
        createdItems.push(newItem);
      }
    }
    
    if (createdItems.length > 0) {
      // Select the newly pasted items
      set({ selectedItemIds: new Set(createdItems.map(i => i.id)) });
    }
    
    return createdItems;
  },
  
  // Check if there are copied items
  hasCopiedItems: () => {
    return get().copiedItems.length > 0;
  },
  
  // Organize: tidy the selection into the grid clusters use, without making a cluster
  autoOrganizeItems: async (selectedItems) => {
    if (selectedItems.length < 2) return;
    const state = get();
    const selected = new Set(selectedItems.map(i => i.id));
    const others = state.items.filter(i => i.board_id === state.currentBoardId && !selected.has(i.id));
    const { arrangeInPlace } = await import('@/lib/clusterLayout');
    const layout = arrangeInPlace(selectedItems, others);

    const before = new Map(selectedItems.map(i => [i.id, { x: i.position_x, y: i.position_y }]));
    const moved = layout.filter(l => {
      const b = before.get(l.id);
      return !b || b.x !== l.position_x || b.y !== l.position_y;
    });
    if (moved.length === 0) return;
    const newPositions = new Map(moved.map(l => [l.id, { x: l.position_x, y: l.position_y }]));
    const previousPositions = new Map(moved.map(l => [l.id, before.get(l.id)!]));
    await get().applyItemPositions(newPositions);
    scheduleProfileRefresh();

    useHistoryStore.getState().pushAction({
      type: 'MOVE_ITEMS',
      timestamp: Date.now(),
      previousPositions,
      newPositions,
    });
  },

  // Board profile
  refreshBoardProfile: async () => {
    const state = get();
    if (!state.currentBoardId || state.items.length === 0 || state.isProfilingBoard) return;

    // Import dynamically to avoid circular deps
    const { getBoardProfile, needsReprofiling, generateBoardProfile } = await import('@/lib/boardProfileService');

    // Profile current board
    const existing = await getBoardProfile(state.currentBoardId);

    if (!needsReprofiling(existing, state.items)) {
      set({ boardProfile: existing });
    } else {
      set({ isProfilingBoard: true });
      profileNeedsRefreshAfterCurrent = false; // reset flag at start
      try {
        const profile = await generateBoardProfile(state.currentBoardId, state.items);
        set({ boardProfile: profile, isProfilingBoard: false });
      } catch {
        set({ isProfilingBoard: false });
      }
      // If changes occurred during profiling, schedule one more refresh
      if (profileNeedsRefreshAfterCurrent) {
        profileNeedsRefreshAfterCurrent = false;
        scheduleProfileRefresh();
      }
    }

    // Background-profile parent and siblings (fire and forget)
    const currentBoard = state.boards.find(b => b.id === state.currentBoardId);
    if (!currentBoard) return;

    const relatedBoardIds: string[] = [];
    if (currentBoard.parent_id) relatedBoardIds.push(currentBoard.parent_id);
    
    const siblings = state.boards.filter(
      b => b.parent_id === currentBoard.parent_id && b.id !== state.currentBoardId
    );
    for (const sib of siblings.slice(0, 3)) {
      relatedBoardIds.push(sib.id);
    }

    // Profile related boards that don't have profiles yet
    for (const boardId of relatedBoardIds) {
      getBoardProfile(boardId).then(async (profile) => {
        if (!profile) {
          // Fetch items for this board and generate profile
          const { data: boardItems } = await supabase
            .from('items')
            .select('*')
            .eq('board_id', boardId)
            .limit(50);
          
          if (boardItems && boardItems.length > 0) {
            await generateBoardProfile(boardId, boardItems as Item[]);
          }
        }
      }).catch(() => { /* Silent fail for background profiling */ });
    }
  },

  // Spatial clustering actions
  detectSpatialClusters: async (opts = {}) => {
    const state = get();
    if (!state.isClusteringEnabled || state.items.length < 2 || !state.currentBoardId) {
      set({ spatialClusters: [], lastClusteredItemIds: [] });
      return;
    }

    // Prevent duplicate calls
    if (state.isDetectingClusters) return;

    const fullItems = state.items.filter(i => i.board_id === state.currentBoardId);
    const currentItemIds = fullItems.map(i => i.id).sort();
    const previousIds = [...state.lastClusteredItemIds].sort();

    // If item set hasn't changed at all, skip entirely
    if (
      currentItemIds.length === previousIds.length &&
      currentItemIds.every((id, idx) => id === previousIds[idx])
    ) {
      return;
    }

    // Determine if this is an incremental update (only new items added)
    const previousSet = new Set(previousIds);
    const newItemIds = currentItemIds.filter(id => !previousSet.has(id));
    const removedIds = previousIds.filter(id => !new Set(currentItemIds).has(id));
    const isIncremental = newItemIds.length > 0 && removedIds.length === 0 && state.spatialClusters.length > 0;

    set({ isDetectingClusters: true });

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session?.access_token) {
        set({ isDetectingClusters: false });
        return;
      }
      
      // Hand-made groups only: draw them, touch nothing else on the board
      if (state.clusterScope === 'manual') {
        const { data: profile } = await supabase
          .from('board_profiles')
          .select('clusters')
          .eq('board_id', state.currentBoardId)
          .maybeSingle();
        const saved = Array.isArray(profile?.clusters) ? (profile.clusters as unknown as AICluster[]) : [];
        const present = new Set(fullItems.map(i => i.id));
        const manual = saved
          .filter(c => c.manual)
          .map(c => ({ ...c, itemIds: c.itemIds.filter(id => present.has(id)) }))
          .filter(c => c.itemIds.length > 0);
        if (manual.length === 0) {
          set({ spatialClusters: [], isClusteringEnabled: false, clusterScope: 'board', isDetectingClusters: false, lastClusteredItemIds: [] });
          return;
        }
        const { buildClustersAsync } = await import('@/workers/clusterWorkerClient');
        const clusters = await buildClustersAsync(manual, fullItems);
        set({ spatialClusters: clusters, isDetectingClusters: false, lastClusteredItemIds: currentItemIds });
        return;
      }

      // Incremental path: only new items added — reuse existing in-memory clusters
      // and assign new items to "Unclustered" without hitting the AI API
      if (isIncremental) {
        const existingAI = state.spatialClusters.map(c => ({
          name: c.name,
          itemIds: [...c.memberItemIds],
          color: c.color,
        }));
        // Add new items to existing "Unclustered" group or create one
        const newContentItems = fullItems.filter(
          i => newItemIds.includes(i.id) && i.item_type !== 'board_link'
        );
        if (newContentItems.length > 0) {
          const unclustered = existingAI.find(c => c.name.toLowerCase() === 'unclustered');
          if (unclustered) {
            unclustered.itemIds.push(...newContentItems.map(i => i.id));
          } else {
            existingAI.push({ name: 'Unclustered', itemIds: newContentItems.map(i => i.id), color: '#E0E0E0' });
          }
        }
        const { buildClustersAsync } = await import('@/workers/clusterWorkerClient');
        const clusters = await buildClustersAsync(existingAI.filter(c => c.itemIds.length > 0), fullItems);
        set({ spatialClusters: clusters, isDetectingClusters: false, lastClusteredItemIds: currentItemIds });
        return;
      }

      // Check if we have saved clusters in board_profiles that match current items
      const { data: profile } = await supabase
        .from('board_profiles')
        .select('clusters')
        .eq('board_id', state.currentBoardId)
        .maybeSingle();
      
      const savedClusters = profile?.clusters as { name: string; itemIds: string[]; color: string }[] | null;
      
      // Check if saved clusters are still valid (all item IDs still exist)
      let useSavedClusters = false;
      if (savedClusters && savedClusters.length > 0) {
        const currentItemIds = new Set(fullItems.map(i => i.id));
        const allSavedItemIds = savedClusters.flatMap(c => c.itemIds);
        const validItems = allSavedItemIds.filter(id => currentItemIds.has(id));
        // Use saved clusters if at least 80% of items are still present
        useSavedClusters = validItems.length >= allSavedItemIds.length * 0.8;
      }

      let aiClusters: { name: string; itemIds: string[]; color: string }[];
      
      if (useSavedClusters && savedClusters) {
        // Filter saved clusters to only include existing items
        const currentItemIds = new Set(fullItems.map(i => i.id));
        const assignedIds = new Set<string>();
        
        aiClusters = savedClusters.map(c => {
          const validIds = c.itemIds.filter(id => currentItemIds.has(id));
          validIds.forEach(id => assignedIds.add(id));
          return { ...c, itemIds: validIds };
        }).filter(c => c.itemIds.length > 0);
        
        // Find items not in any cluster and add them to "Unclustered"
        // Only include content items (not board_link items)
        const unassignedItems = fullItems.filter(i => 
          !assignedIds.has(i.id) && i.item_type !== 'board_link'
        );
        if (unassignedItems.length > 0) {
          aiClusters.push({
            name: 'Unclustered',
            itemIds: unassignedItems.map(i => i.id),
            color: '#E0E0E0',
          });
        }
      } else {
        // Generate new clusters from AI
        // Filter out board_link items - they're navigation, not content to cluster
        const contentItems = fullItems.filter(i => i.item_type !== 'board_link');
        const itemsPayload = contentItems.map(i => ({
          id: i.id,
          item_type: i.item_type,
          content: i.content,
        }));

        if (itemsPayload.length < 2) {
          // Not enough content items for AI — but board_link items may still form a cluster
          aiClusters = [];
        } else {
          // Task 4.3: Check cluster_cache before calling AI
          // Table not yet in generated Supabase types — use untyped call
          const contentHash = computeClusterContentHash(contentItems.map(i => i.id));
          const { data: cachedResult } = await (supabase as any)
            .from('cluster_cache')
            .select('clusters')
            .eq('board_id', state.currentBoardId!)
            .eq('content_hash', contentHash)
            .maybeSingle();

          if (cachedResult?.clusters) {
            console.log('[Clustering] Cache hit — reusing cached clusters');
            aiClusters = keepManualClusters(
              cachedResult.clusters as AICluster[],
              savedClusters,
              new Set(fullItems.map(i => i.id)),
            );
          } else {
          // Cache miss — call AI edge function
          const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/board-clusters`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.data.session.access_token}`,
            },
            body: JSON.stringify({
              boardId: state.currentBoardId,
              items: itemsPayload,
              boardProfile: state.boardProfile || null,
              previousClusters: savedClusters || null,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[Clustering] API error:', response.status, errorData);
          throw new Error(errorData.error || 'Cluster detection failed');
        }

        const data = await response.json();
        // The user's own groups survive a re-run
        aiClusters = keepManualClusters(data.clusters || [], savedClusters, new Set(fullItems.map(i => i.id)));

        // Save clusters to board profile in DB
        await supabase
          .from('board_profiles')
          .update({ clusters: aiClusters })
          .eq('board_id', state.currentBoardId);

        // Populate graph relationships for cluster co-membership (fire-and-forget)
        try {
          const userId = session.data.session.user.id;
          const clusterRelationships: Array<{
            source_item_id: string;
            target_item_id: string;
            user_id: string;
            relationship_type: string;
            strength: number;
            metadata: { cluster_name: string };
          }> = [];
          for (const cluster of aiClusters) {
            // Cap pairwise edges: for large clusters only connect first 8 items
            const ids = cluster.itemIds.slice(0, 8);
            for (let i = 0; i < ids.length; i++) {
              for (let j = i + 1; j < ids.length; j++) {
                clusterRelationships.push({
                  source_item_id: ids[i],
                  target_item_id: ids[j],
                  user_id: userId,
                  relationship_type: 'same_cluster',
                  strength: 0.7,
                  metadata: { cluster_name: cluster.name },
                });
              }
            }
          }
          // Table not yet in generated Supabase types — use untyped call
          if (clusterRelationships.length > 0) {
            (supabase as any).from('item_relationships')
              .upsert(clusterRelationships, { onConflict: 'source_item_id,target_item_id,relationship_type' })
              .then(({ error: relErr }: any) => {
                if (relErr) console.warn('[Clustering] Failed to upsert cluster relationships:', relErr);
              });
          }
        } catch (relErr) {
          console.warn('[Clustering] Graph relationship population failed:', relErr);
        }

        // Task 4.3: Write to cluster_cache for next time
        // Table not yet in generated Supabase types — use untyped call
        (supabase as any)
          .from('cluster_cache')
          .upsert(
            { board_id: state.currentBoardId!, content_hash: contentHash, clusters: aiClusters, updated_at: new Date().toISOString() },
            { onConflict: 'board_id,content_hash' },
          )
          .then(({ error: cacheErr }: any) => {
            if (cacheErr) console.warn('[Clustering] Cache write failed:', cacheErr);
          });

          } // end cache miss else
        }
      }

      // Add a "Boards" cluster for board_link items
      const boardLinkItems = fullItems.filter(i => i.item_type === 'board_link');
      if (boardLinkItems.length > 0) {
        const boardLinkIds = new Set(boardLinkItems.map(i => i.id));
        // Remove any board_link items that leaked into AI/saved clusters
        aiClusters = aiClusters
          .map(c => ({ ...c, itemIds: c.itemIds.filter(id => !boardLinkIds.has(id)) }))
          .filter(c => c.itemIds.length > 0);
        // Add dedicated Boards cluster
        aiClusters.push({ name: 'Boards', itemIds: boardLinkItems.map(i => i.id), color: '#4A9EFF' });
      }

      if (aiClusters.length === 0) {
        set({ spatialClusters: [], isDetectingClusters: false, lastClusteredItemIds: currentItemIds });
        return;
      }

      // Build full SpatialCluster objects with bounding boxes (off main thread)
      const { buildClustersAsync, computeClusterLayoutAsync } = await import('@/workers/clusterWorkerClient');
      const clusters = await buildClustersAsync(aiClusters, fullItems);

      set({ spatialClusters: clusters, isDetectingClusters: false, lastClusteredItemIds: currentItemIds });

      // Auto-arrange items into cluster groups (off main thread)
      if (clusters.length > 0 && opts.layout !== false) {
        const layout = await computeClusterLayoutAsync(clusters, fullItems);

        // Build position map for fast lookup
        const positionMap = new Map(
          layout.itemPositions.map(l => [l.id, { x: l.position_x, y: l.position_y }])
        );

        // Single atomic state update for BOTH items and allItems
        const currentState = get();
        const updatedItems = currentState.items.map(item => {
          const newPos = positionMap.get(item.id);
          if (newPos) {
            return { ...item, position_x: newPos.x, position_y: newPos.y };
          }
          return item;
        });
        const updatedAllItems = currentState.allItems.map(item => {
          const newPos = positionMap.get(item.id);
          if (newPos) {
            return { ...item, position_x: newPos.x, position_y: newPos.y };
          }
          return item;
        });

        // CRITICAL: Set isAutoOrganizing AND new positions in ONE atomic call
        // Also trigger fit-to-view so user can see the clustered items
        set({ items: updatedItems, allItems: updatedAllItems, isAutoOrganizing: true, shouldFitToView: true });

        // Batch persist to DB — single upsert instead of N individual calls
        if (layout.itemPositions.length > 0) {
          set({ isSaving: true });
          const upsertData = layout.itemPositions.map(pos => ({
            id: pos.id,
            position_x: pos.position_x,
            position_y: pos.position_y,
          }));
          const { error } = await supabase
            .from('items')
            .upsert(upsertData, { onConflict: 'id', ignoreDuplicates: false });
          if (error) console.error('Error batch-saving cluster positions:', error);
          set({ isSaving: false });
        }

        setTimeout(() => set({ isAutoOrganizing: false }), 600);
      }

    } catch (error) {
      console.error('[Clustering] Error:', error);
      toast.error('Failed to analyze content clusters');
      set({ isDetectingClusters: false });
    }
  },

  setClusteringEnabled: (enabled) => {
    // The toggle always means the whole board, even after a hand-made group
    set({ isClusteringEnabled: enabled, clusterScope: 'board' });
    if (enabled) {
      set({ spatialClusters: [], lastClusteredItemIds: [] });
      get().detectSpatialClusters();
    } else {
      set({ spatialClusters: [], isDetectingClusters: false, lastClusteredItemIds: [] });
    }
  },

  setSpatialClusters: (clusters) => {
    set({ spatialClusters: clusters });
  },

  getClusterForItem: (itemId: string): SpatialCluster | null => {
    const state = get();
    return state.spatialClusters.find(c => c.memberItemIds.includes(itemId)) || null;
  },

  getClusterMembers: (clusterId: string): Item[] => {
    const state = get();
    const cluster = state.spatialClusters.find(c => c.id === clusterId);
    if (!cluster) return [];
    return state.items.filter(i => cluster.memberItemIds.includes(i.id));
  },

  moveItemToCluster: (itemId: string, targetClusterId: string | null) => {
    const state = get();
    const item = state.items.find(i => i.id === itemId);
    if (!item) return;

    const updatedClusters: SpatialCluster[] = [];

    for (const cluster of state.spatialClusters) {
      const isMember = cluster.memberItemIds.includes(itemId);
      const isTarget = cluster.id === targetClusterId;

      if (isMember && !isTarget) {
        // Remove item from this cluster
        const newMemberIds = cluster.memberItemIds.filter(id => id !== itemId);
        if (newMemberIds.length === 0) continue; // Drop empty clusters

        const members = state.items.filter(i => newMemberIds.includes(i.id));
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const m of members) {
          minX = Math.min(minX, m.position_x);
          minY = Math.min(minY, m.position_y);
          maxX = Math.max(maxX, m.position_x + m.width);
          maxY = Math.max(maxY, m.position_y + m.height);
        }
        const width = maxX - minX;
        const height = maxY - minY;

        updatedClusters.push({
          ...cluster,
          memberItemIds: newMemberIds,
          boundingBox: { minX, minY, maxX, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2, width, height },
          density: newMemberIds.length / (Math.max(width * height, 1) / 1000),
        });
      } else if (isTarget && !isMember) {
        // Add item to this cluster
        const newMemberIds = [...cluster.memberItemIds, itemId];

        const members = state.items.filter(i => newMemberIds.includes(i.id));
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const m of members) {
          minX = Math.min(minX, m.position_x);
          minY = Math.min(minY, m.position_y);
          maxX = Math.max(maxX, m.position_x + m.width);
          maxY = Math.max(maxY, m.position_y + m.height);
        }
        const width = maxX - minX;
        const height = maxY - minY;

        updatedClusters.push({
          ...cluster,
          memberItemIds: newMemberIds,
          boundingBox: { minX, minY, maxX, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2, width, height },
          density: newMemberIds.length / (Math.max(width * height, 1) / 1000),
        });
      } else {
        updatedClusters.push(cluster);
      }
    }

    set({ spatialClusters: updatedClusters });

    // Saved with the board, so a reload or the next re-cluster doesn't put the card back
    const boardId = state.currentBoardId;
    if (!boardId) return;
    const anchorId = updatedClusters.find(c => c.id === targetClusterId)?.memberItemIds.find(id => id !== itemId) ?? null;
    void loadBoardClusters(boardId).then(saved => {
      if (saved.length > 0) return saveBoardClusters(boardId, moveBetweenClusters(saved, itemId, anchorId));
    });
  },

  groupIntoCluster: async (itemIds: string[]) => {
    const state = get();
    const boardId = state.currentBoardId;
    if (!boardId) return;
    // Board cards always go to the "Boards" group, so they can't be grouped by hand
    const groupable = new Set(
      state.items.filter(i => i.board_id === boardId && i.item_type !== 'board_link').map(i => i.id),
    );
    const ids = itemIds.filter(id => groupable.has(id));
    if (ids.length < 2) {
      toast.info('Select two or more cards to group them into a cluster.');
      return;
    }

    // Start from the clusters on screen, else the ones saved with the board
    let base: AICluster[] = toSavedClusters(state.spatialClusters);
    if (base.length === 0) {
      const { data: profile } = await supabase
        .from('board_profiles')
        .select('clusters')
        .eq('board_id', boardId)
        .maybeSingle();
      base = Array.isArray(profile?.clusters) ? (profile.clusters as unknown as AICluster[]) : [];
    }

    const next = groupIntoManualCluster(base, ids);
    if (!(await saveBoardClusters(boardId, next))) {
      toast.error("Couldn't save the cluster. Please try again.");
      return;
    }

    // Tidy only the grouped cards, where they are. The rest of the board stays put.
    const grouped = new Set(ids);
    const members = state.items.filter(i => grouped.has(i.id));
    const others = state.items.filter(i => i.board_id === boardId && !grouped.has(i.id));
    const { arrangeInPlace } = await import('@/lib/clusterLayout');
    const layout = arrangeInPlace(members, others);
    const previousPositions = new Map(members.map(m => [m.id, { x: m.position_x, y: m.position_y }]));
    const newPositions = new Map(layout.map(l => [l.id, { x: l.position_x, y: l.position_y }]));
    await get().applyItemPositions(newPositions);

    // Clustering off before: show just the hand-made groups. On: keep the whole-board view.
    const previousClustering = { enabled: state.isClusteringEnabled, scope: state.clusterScope };
    const newClustering = {
      enabled: true,
      scope: state.isClusteringEnabled ? state.clusterScope : ('manual' as const),
    };
    set({
      isClusteringEnabled: true,
      clusterScope: newClustering.scope,
      spatialClusters: [],
      lastClusteredItemIds: [],
      clusterToRename: clusterIdFor(ids),
    });
    await get().detectSpatialClusters({ layout: false });

    useHistoryStore.getState().pushAction({
      type: 'GROUP_CLUSTER',
      timestamp: Date.now(),
      itemIds: ids,
      previousPositions,
      newPositions,
      previousClusters: base,
      newClusters: next,
      previousClustering,
      newClustering,
    });
  },

  restoreClusters: async (saved, clustering) => {
    const boardId = get().currentBoardId;
    if (!boardId) return;
    await saveBoardClusters(boardId, saved);
    set({
      isClusteringEnabled: clustering.enabled,
      clusterScope: clustering.enabled ? clustering.scope : 'board',
      spatialClusters: [],
      lastClusteredItemIds: [],
      clusterToRename: null,
    });
    if (clustering.enabled) await get().detectSpatialClusters({ layout: false });
  },

  ungroupCluster: async (clusterId: string) => {
    const state = get();
    const boardId = state.currentBoardId;
    const cluster = state.spatialClusters.find(c => c.id === clusterId);
    if (!boardId || !cluster) return;

    // Start from the clusters saved with the board, else the ones on screen
    let base = await loadBoardClusters(boardId);
    if (base.length === 0) base = toSavedClusters(state.spatialClusters);
    const next = removeFromClusters(base, cluster.memberItemIds);
    if (!(await saveBoardClusters(boardId, next))) {
      toast.error("Couldn't ungroup the cluster. Please try again.");
      return;
    }

    // The cards stay put, just without the group around them. Showing only
    // hand-made groups and this was the last one: clustering goes off.
    const remaining = get().spatialClusters.filter(c => c.id !== clusterId);
    const previousClustering = { enabled: state.isClusteringEnabled, scope: state.clusterScope };
    const newClustering = state.clusterScope === 'manual' && remaining.length === 0
      ? { enabled: false, scope: 'board' as const }
      : previousClustering;
    set({
      spatialClusters: remaining,
      isClusteringEnabled: newClustering.enabled,
      clusterScope: newClustering.scope,
      highlightedClusterId: null,
      ...(newClustering.enabled ? {} : { lastClusteredItemIds: [] }),
    });

    useHistoryStore.getState().pushAction({
      type: 'UNGROUP_CLUSTER',
      timestamp: Date.now(),
      itemIds: [...cluster.memberItemIds],
      previousClusters: base,
      newClusters: next,
      previousClustering,
      newClustering,
    });
  },

  applyItemPositions: async (positions) => {
    if (positions.size === 0) return;
    const move = (item: Item) => {
      const p = positions.get(item.id);
      return p ? { ...item, position_x: p.x, position_y: p.y } : item;
    };
    // Positions and isAutoOrganizing in one update, so the cards animate there
    set(s => ({ items: s.items.map(move), allItems: s.allItems.map(move), isAutoOrganizing: true }));
    // Through the mutation queue, so a drag still waiting to save can't land
    // after these and put the cards back where they were
    const { enqueueMutation, flush } = await import('@/lib/mutationQueue');
    for (const [id, p] of positions) {
      enqueueMutation(id, { position_x: p.x, position_y: p.y }, 'position');
    }
    await flush();
    setTimeout(() => set({ isAutoOrganizing: false }), 600);
  },

  persistClusters: async () => {
    const { currentBoardId, spatialClusters } = get();
    if (!currentBoardId || spatialClusters.length === 0) return;
    await saveBoardClusters(currentBoardId, toSavedClusters(spatialClusters));
  },

  setClusterToRename: (clusterId) => set({ clusterToRename: clusterId }),

  rescueStrandedItems: (): number => {
    const state = get();
    const { items, currentBoardId, updateItem } = state;

    const boardItems = items.filter(item => item.board_id === currentBoardId);
    // Only rescue items at truly impossible positions (NaN, Infinity, or far off-screen)
    const strandedItems = boardItems.filter(item => {
      const x = item.position_x ?? 0;
      const y = item.position_y ?? 0;
      return !Number.isFinite(x) || !Number.isFinite(y) || x < -5000 || y < -5000;
    });

    strandedItems.forEach(item => {
      updateItem(item.id, {
        position_x: 100,
        position_y: 100,
      });
    });

    return strandedItems.length;
  },
}));

// Wire up the lazy getter now that the store exists
_getCanvasStore = useCanvasStore.getState;

// Subject labels from tag-subjects land in local state without a write back
// (the function already saved them)
onSubjectsTagged((results) => {
  const byId = new Map(results.map(r => [r.id, r.subjects]));
  const patch = (list: Item[]) => list.some(i => byId.has(i.id))
    ? list.map(i => byId.has(i.id) ? { ...i, subjects: byId.get(i.id)! } : i)
    : list;
  const { items, allItems } = useCanvasStore.getState();
  useCanvasStore.setState({ items: patch(items), allItems: patch(allItems) });
});
